// #1718 — member rank as a dated, derived record.
//
// A `users/{uid}/rankChanges/{id}` row mirrors `statusChanges` (see
// ./memberStatus.js): append-only, soft-deleted, carrying an `effectiveAt` (the
// date the promotion actually took effect) alongside `changedAt` (the server
// clock when an officer typed it in). `users/{uid}.role` stays a MIRROR of
// whatever `resolveEffectiveRank` says — it is never the system of record.
//
// The two invariants from memberStatus.js apply here unchanged:
//
// 1. `effectiveAt` may be MISSING (rows created by the per-tenant backfill
//    before it stamped one, or by an older client). Every read path falls back
//    to `effectiveAt ?? changedAt`. Never sort these rows in Firestore with
//    `orderBy('effectiveAt')` — Firestore silently excludes docs missing the
//    ordered field. Read the (tiny) subcollection unordered and sort here.
//
// 2. Rows are soft-deleted (`deletedAt`), never removed. Every helper drops
//    tombstoned rows.
//
// Plus two that are specific to rank, both caused by `effectiveAtType`:
//
// 3. TIMEZONE. `statusChanges` stores a browser-local start-of-day and never
//    re-normalizes server-side (Cloud Run is UTC, so a `startOf('day')` there
//    would shift the calendar day). That convention is safe for day precision
//    only. A member who knows they made Captain "sometime in March 2009" picks
//    month precision, and a local Mar 1 in UTC+13 is Feb 28Z — a server-side
//    `startOf('month')` would land in February. So `rankChanges` DIVERGES: the
//    client sends `effectiveAt` as a date-only `YYYY-MM-DD` string and the api
//    normalizes it in UTC (`normalizeToPrecision` below). Everything stored is
//    therefore a UTC start-of-day/month/year, and everything formats in UTC.
//
// 4. PRECISION. `effectiveAtType` is 'day' | 'month' | 'year' — how precisely
//    the department knows the date. It is NOT `effectiveAtInferred`, which means
//    "we guessed this from something else". "March 2009" is known to the month;
//    "changedAt, because that's all the audit log had" is inferred.
//
//    Two rows can normalize to the same instant at different precisions (a
//    year-2009 row and a day-Jan-1-2009 row both land on Jan 1 2009Z). The sort
//    breaks that tie COARSEST FIRST, so "2009" reads as the opening state and
//    the specific January date supersedes it.
//
// Pure module: no firebase, no moment. `toDateValue` is reused from
// memberStatus.js so both resolvers duck-type dates identically.

const {toDateValue} = require('./memberStatus');

const PRECISIONS = ['year', 'month', 'day'];

// Coarsest first. Used both to sort ties and to pick the looser of two
// precisions when comparing rows.
const PRECISION_ORDER = {year: 0, month: 1, day: 2};

const DEFAULT_PRECISION = 'day';

const time = date => (date ? date.getTime() : 0);

// Anything unrecognized (undefined on a legacy row, a typo, null) reads as day
// precision — the old behaviour, and the safest default: it never claims less
// certainty than the officer actually expressed.
const precisionOf = value => {
    const type = typeof value === 'object' && value !== null ? value.effectiveAtType : value;
    return PRECISIONS.includes(type) ? type : DEFAULT_PRECISION;
};

// The coarser (less precise) of two precisions — 'year' beats 'month' beats
// 'day'. Duplicate detection compares at this level, or "Captain, Mar 2009" and
// "Captain, Mar 15 2009" both save and the same promotion shows twice.
const coarserPrecision = (a, b) =>
    (PRECISION_ORDER[precisionOf(a)] <= PRECISION_ORDER[precisionOf(b)] ? precisionOf(a) : precisionOf(b));

// Truncate to the start of the day/month/year IN UTC. See invariant 3: these
// values are stored and rendered in UTC end to end, so a local truncation would
// move the calendar day for tenants east of UTC.
const normalizeToPrecision = (value, type = DEFAULT_PRECISION) => {
    const date = toDateValue(value);
    if (!date) return null;

    const precision = precisionOf(type);
    const year = date.getUTCFullYear();
    const month = precision === 'year' ? 0 : date.getUTCMonth();
    const day = precision === 'day' ? date.getUTCDate() : 1;

    return new Date(Date.UTC(year, month, day));
};

// Do two dates refer to the same period once both are truncated to `precision`?
const sameInstantAtPrecision = (a, b, precision = DEFAULT_PRECISION) => {
    const left = normalizeToPrecision(a, precision);
    const right = normalizeToPrecision(b, precision);
    return Boolean(left && right) && left.getTime() === right.getTime();
};

// Drop tombstoned + undatable rows, normalize dates, sort ascending.
//
// Sort key mirrors resolveEffectiveRank exactly (effectiveAt, then precision
// coarsest-first, then changedAt, then id) so "the last row" here and "the
// greatest row" there can never disagree.
const normalizeRankChanges = rows => {
    const normalized = [];

    (rows || []).forEach(row => {
        if (!row || row.deletedAt) return;

        const changedAt = toDateValue(row.changedAt);
        const effectiveAtType = precisionOf(row.effectiveAtType);
        // Stored values are already normalized by the api, but a row written by
        // the backfill or an older client may not be — truncating again is
        // idempotent and makes the comparator total.
        const effectiveAt = normalizeToPrecision(row.effectiveAt || changedAt, effectiveAtType);
        // A row with neither date can't be placed on a timeline. The backfill
        // reports these rather than guessing at one.
        if (!effectiveAt) return;

        normalized.push({
            ...row,
            id: row.id || null,
            rank: row.rank || null,
            effectiveAt,
            effectiveAtType,
            changedAt,
            inferred: row.effectiveAtInferred === true || !row.effectiveAt
        });
    });

    normalized.sort((a, b) =>
        time(a.effectiveAt) - time(b.effectiveAt) ||
        PRECISION_ORDER[a.effectiveAtType] - PRECISION_ORDER[b.effectiveAtType] ||
        time(a.changedAt) - time(b.changedAt) ||
        String(a.id || '').localeCompare(String(b.id || '')));

    return normalized;
};

// The rank in force at `at` — the greatest `effectiveAt <= at`.
//
// An EMPTY set resolves to `{rank: null, inferred: true}`, never a fallback
// rank. Same reasoning as recomputeUserStatus's empty-subcollection guard: a
// member whose rank predates #1718 has no rows at all, and inventing a rank here
// would let the nightly sweep overwrite a real `users/{uid}.role` with a guess.
const resolveEffectiveRank = (rows, options = {}) => {
    const {at = new Date()} = options;

    const asOf = toDateValue(at) || new Date();
    const normalized = normalizeRankChanges(rows);

    let resolved = null;
    for (const row of normalized) {
        if (time(row.effectiveAt) > time(asOf)) break;
        resolved = row;
    }

    if (!resolved) {
        return {
            rank: null,
            effectiveAt: null,
            effectiveAtType: DEFAULT_PRECISION,
            changedAt: null,
            changedBy: null,
            sourceId: null,
            inferred: true
        };
    }

    return {
        rank: resolved.rank,
        effectiveAt: resolved.effectiveAt,
        effectiveAtType: resolved.effectiveAtType,
        changedAt: resolved.changedAt,
        changedBy: resolved.changedBy || null,
        sourceId: resolved.id || null,
        inferred: resolved.inferred === true
    };
};

// The sorted rows with `previousRank` + `direction` DERIVED from their
// neighbours rather than read off the row.
//
// Nothing stores `previousRank`: it is only correct at the instant it's written,
// and a backdated insert, a date edit or a delete rots its neighbours' copies.
// Deriving costs one pass over a handful of rows.
//
// `directionOf` is injected (common/index.js passes `rankChangeDirection`) to
// keep this module free of the rank tables — the ordering is department policy,
// the timeline shape isn't.
// An acting or interim row says who is COVERING a rank, not who holds it. A
// temporary appointment has no recorded end, so letting one answer "what rank is
// this member" would promote somebody permanently for two weeks of shift
// cover — and would count that cover as a promotion on a board report.
//
// Lives here rather than in the app because the report models read it too;
// `src/data/serviceHistory.js` re-exports these, and `api/src/data/memberRank.js`
// applies the same rule server-side.
const TEMPORARY_APPOINTMENTS = ['acting', 'interim'];

// Anything unrecognized — including a row written before the field existed —
// reads as permanent.
const appointmentTypeOf = row => {
    const type = row && row.appointmentType;
    return ['permanent', ...TEMPORARY_APPOINTMENTS].includes(type) ? type : 'permanent';
};

const isTemporaryAppointment = row => TEMPORARY_APPOINTMENTS.includes(appointmentTypeOf(row));

// Rank rows that say what rank the member HOLDS — every row except an acting or
// interim one. Temporary rows are still displayed elsewhere (the acting track on
// the Service History tab); they just never move the ladder.
const permanentRanks = rows => (rows || []).filter(row => !isTemporaryAppointment(row));

const rankTimeline = (rows, options = {}) => {
    const {directionOf = null} = options;
    const normalized = normalizeRankChanges(rows);

    let previousRank = null;
    return normalized.map(row => {
        const entry = {
            ...row,
            previousRank,
            direction: directionOf ? directionOf(previousRank, row.rank) : (previousRank ? null : 'initial')
        };
        previousRank = row.rank;
        return entry;
    });
};

module.exports = {
    PRECISIONS,
    TEMPORARY_APPOINTMENTS,
    appointmentTypeOf,
    isTemporaryAppointment,
    permanentRanks,
    precisionOf,
    coarserPrecision,
    normalizeToPrecision,
    sameInstantAtPrecision,
    normalizeRankChanges,
    resolveEffectiveRank,
    rankTimeline
};
